import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { trainingAPI, projectsAPI, exportAPI, downloadBlob } from '../utils/api';
import { PageHeader, Modal, FormField, EmptyState, ConfirmDialog } from '../components/layout/UI';
import { format } from 'date-fns';
import { apiError, useConfirm, useToast } from '../components/Toast';
import { usePagePerms } from '../PagePermsContext';
import { Clock, Download, GraduationCap, Plus, RotateCcw, SquarePen, Star, Trash2 } from 'lucide-react';
import Pager, { usePaging } from '../components/Pager';
import { useSelection, SelectAll, RowCheck, BulkBar } from '../components/BulkActions';

// Inline SVGs — no lucide imports
const DlIcon = () => <Download size={14} />;
const PlusIcon = () => <Plus size={14} />;
const PencilIcon = () => <SquarePen size={13} />;
const TrashIcon = () => <Trash2 size={13} />;
const ClockIcon = () => <Clock size={18} />;

const ScoreBar = ({ score }) => {
  const pct = score ? (score / 10) * 100 : 0;
  const color = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-medium w-8" style={{ color: 'var(--text-secondary)' }}>{score ?? '—'}</span>
    </div>
  );
};

const defaultForm = { project_id: '', session_name: '', session_date: '', participants: '', topics_covered: '', duration_minutes: '', feedback_score: '', notes: '' };

export default function TrainingPage() {
  // Admin → Page Access decides whether this role may create here.
  const { canWrite } = usePagePerms();
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('project_id') || '';
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [form, setForm] = useState({ ...defaultForm, project_id: projectFilter });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // All / Deleted. The backend has supported this since phase58; this page
  // was the last one without it, so a deleted session was unreachable.
  const [scope, setScope] = useState('all');
  // One pager for every list, so they all count and phrase it the same way.
  const paging = usePaging(scope);


  // Ticked rows. Scoped to what is on screen, so a filter cannot hide
  // something that is still selected.
  const sel = useSelection(sessions);

  const load = useCallback(async () => {
    try {
      const [sRes, pRes] = await Promise.all([
        trainingAPI.getAll({ project_id: projectFilter,
                             scope: scope === 'deleted' ? 'deleted' : undefined,
                             mine:  scope === 'mine' ? 'true' : undefined, limit: paging.limit, offset: paging.offset }),
        projectsAPI.getAll({ limit: 0 })
      ]);
      setSessions(sRes.data);
      paging.readHeaders(sRes);
      setProjects(pRes.data);
    } catch(err) { toast.error(apiError(err, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [projectFilter, scope, paging.offset]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditSession(null); setForm({ ...defaultForm, project_id: projectFilter }); setFiles([]); setModalOpen(true); };

  const restoreSession = async (s) => {
    try {
      await trainingAPI.restore(s.id);
      toast.success('Restored');
      load();
    } catch (e) { toast.error(apiError(e, 'Could not restore that session.')); }
  };

  const discardSession = async (s) => {
    const ok = await confirm(
      'Remove this from your Deleted tab? You will not be able to see it after '
      + 'this, though an administrator can still restore it.',
      { title: 'Remove from Deleted', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await trainingAPI.discard(s.id);
      toast.success('Removed from your Deleted tab');
      load();
    } catch (e) { toast.error(apiError(e, 'Could not remove it.')); }
  };
  const openEdit = (s) => {
    setEditSession(s);
    setForm({
      project_id: s.project_id, session_name: s.session_name,
      session_date: s.session_date?.split('T')[0] || '',
      participants: (s.participants || []).join(', '),
      topics_covered: s.topics_covered || '', duration_minutes: s.duration_minutes || '',
      feedback_score: s.feedback_score || '', notes: s.notes || ''
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editSession) {
        await trainingAPI.update(editSession.id, form);
        toast.success('Training session updated');
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        files.forEach(f => fd.append('files', f));
        await trainingAPI.create(fd);
        toast.success('Training session created');
      }
      setModalOpen(false);
      load();
    } catch (err) { toast.error(apiError(err, 'Failed to save training session. Please try again.')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await trainingAPI.delete(deleteId);
    toast.success('Training session deleted'); setDeleteId(null); load(); } catch (err) { toast.error(apiError(err, 'Failed to delete training session')); }
  };

  const avgScore = sessions.filter(s => s.feedback_score).reduce((acc, s, _, arr) => acc + s.feedback_score / arr.length, 0);
  const totalHours = sessions.reduce((acc, s) => acc + (s.duration_minutes || 0), 0);

  return (
    <div>
      <PageHeader
        title="Training Sessions"
        subtitle="Track all training and onboarding sessions"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={async()=>{ const r=await exportAPI.exportTraining(projectFilter); downloadBlob(r.data,'training.xlsx'); }} className="btn-secondary flex items-center gap-1.5 text-sm"><DlIcon />
 Export</button>
            {canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIcon /> Log Session</button>)}
          </div>
        }
      />

      {/* All / Mine / Deleted. This was inside the Export button, so every
          click on it exported a spreadsheet — the <button> inside <button>
          warning in your console was exactly this. */}
      <div className="flex rounded-lg border overflow-hidden text-sm mb-4"
        style={{ borderColor:'var(--border)', width:'fit-content' }}>
        {[['all','All'],['mine','Mine'],['deleted','Deleted']].map(([v,l]) => (
          <button key={v} onClick={() => setScope(v)}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${scope===v ? 'bg-brand-500 text-white' : ''}`}
            style={scope!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
            {v === 'deleted' && <Trash2 size={13} />}
            {l}
            {scope===v && <span className="font-bold">{sessions.length}</span>}
          </button>
        ))}
      </div>


      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Sessions', value: sessions.length, icon: <GraduationCap size={18}/>, color: '#8b5cf6' },
          { label: 'Avg Feedback', value: avgScore ? avgScore.toFixed(1) : '—', icon: <Star size={18}/>, color: '#f59e0b' },
          { label: 'Total Hours', value: `${(totalHours / 60).toFixed(1)}h`, icon: <ClockIcon />, color: '#3b82f6' },
        ].map(card => (
          <div key={card.label} className="card flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${card.color}20`, color: card.color }}>
              {card.icon}
            </div>
            <div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{card.label}</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={GraduationCap}
            title="No training sessions"
            description="Log your first training session"
            action={canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIcon /> Log Session</button>)}
          />
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  <th className="px-4 py-3 w-10">
                    <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                      onToggle={sel.toggleAll} />
                  </th>
                  {['Session','Project','Date','Duration','Participants','Score','Topics','Actions'].map(h=>(
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id}
                    className="border-b last:border-0 hover:bg-[var(--surface-alt)] cursor-pointer transition-colors group"
                    style={{ borderColor:'var(--border)' }}
                    onClick={() => navigate(`/training/${s.id}`)}>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <RowCheck checked={sel.isSelected(s.id)}
                        onToggle={() => sel.toggle(s.id)} label={s.title || s.topic} />
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-semibold" style={{ color:'var(--text)' }}>{s.session_name}</p>
                    </td>
                    <td className="py-3 px-4 text-xs" style={{ color:'var(--text-secondary)' }}>
                      {s.project_name || '—'}
                    </td>
                    <td className="py-3 px-4 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                      {s.session_date ? format(new Date(s.session_date), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="py-3 px-4 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                      {s.duration_minutes ? `${s.duration_minutes} min` : '—'}
                    </td>
                    <td className="py-3 px-4 text-xs" style={{ color:'var(--text-secondary)' }}>
                      {s.participants?.length > 0 ? (
                        <div>
                          <p className="font-medium" style={{ color:'var(--text)' }}>{s.participants.length}</p>
                          <p className="text-xs opacity-70">{s.participants.slice(0,2).join(', ')}{s.participants.length>2?` +${s.participants.length-2} more`:''}</p>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="py-3 px-4">
                      {s.feedback_score != null ? (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                            <div className="h-full rounded-full" style={{ width:`${s.feedback_score*10}%`, background:s.feedback_score>=8?'#10b981':s.feedback_score>=6?'#f59e0b':'#ef4444' }}/>
                          </div>
                          <span className="text-xs font-bold" style={{ color:s.feedback_score>=8?'#10b981':s.feedback_score>=6?'#f59e0b':'#ef4444' }}>{s.feedback_score}/10</span>
                        </div>
                      ) : <span className="text-xs" style={{ color:'var(--text-secondary)' }}>—</span>}
                    </td>
                    <td className="py-3 px-4 text-xs max-w-xs">
                      <p className="truncate" style={{ color:'var(--text-secondary)' }}>{s.topics_covered || '—'}</p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                        {scope === 'deleted' ? (
                          <>
                            {/* In the bin, put it back or take it out of your own Deleted tab. */}
                            <button onClick={e=>{e.stopPropagation();restoreSession(s);}}
                              className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-[var(--surface-alt)]"
                              style={{ borderColor:'var(--border)', color:'#2E539F' }}>
                              <RotateCcw size={12} /> Restore
                            </button>
                            <button onClick={e=>{e.stopPropagation();discardSession(s);}}
                              className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-red-50 dark:hover:bg-red-900/20"
                              style={{ borderColor:'var(--border)', color:'#ef4444' }}>
                              <Trash2 size={12} /> Delete
                            </button>
                          </>
                        ) : (
                          <>
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded transition-colors hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><PencilIcon /></button>
                        <button onClick={() => setDeleteId(s.id)} className="p-1.5 rounded text-red-400 hover:bg-red-50 transition-colors"><TrashIcon /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editSession ? 'Edit Training Session' : 'Log Training Session'} size="lg">
        <div className="space-y-4">
          <FormField label="Project" required>
            <select className="select" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>
              <option value="">Select project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Session Name" required>
              <input className="input" value={form.session_name} onChange={e => setForm(f => ({ ...f, session_name: e.target.value }))} placeholder="Session title" />
            </FormField>
            <FormField label="Date" required>
              <input type="date" className="input" value={form.session_date} onChange={e => setForm(f => ({ ...f, session_date: e.target.value }))} />
            </FormField>
          </div>
          <FormField label="Participants (comma-separated)">
            <input className="input" value={form.participants} onChange={e => setForm(f => ({ ...f, participants: e.target.value }))} placeholder="John Doe, Jane Smith, ..." />
          </FormField>
          <FormField label="Topics Covered">
            <textarea className="input" rows={3} value={form.topics_covered} onChange={e => setForm(f => ({ ...f, topics_covered: e.target.value }))} placeholder="Topics covered..." />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Duration (minutes)">
              <input type="number" className="input" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} placeholder="60" min="0" />
            </FormField>
            <FormField label="Feedback Score (0-10)">
              <input type="number" className="input" value={form.feedback_score} onChange={e => setForm(f => ({ ...f, feedback_score: e.target.value }))} placeholder="8.5" min="0" max="10" step="0.1" />
            </FormField>
          </div>
          <FormField label="Notes">
            <textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes..." />
          </FormField>
          {!editSession && (
            <FormField label="Attachments">
              <input type="file" multiple className="input" onChange={e => setFiles([...e.target.files])} />
            </FormField>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.project_id || !form.session_name || !form.session_date} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin" />}
              {editSession ? 'Update' : 'Log'} Session
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Delete Session" message="Delete this training session permanently?" />

    <Pager {...paging} onChange={paging.setOffset} noun="sessions" />
    <BulkBar bulk={trainingAPI.bulk} view={scope === 'deleted' ? 'deleted' : 'live'}
      selected={sel.selected} label="session" labelPlural="sessions"
      onClear={sel.clear} onDone={load} />
    </div>
  );
}
